import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
  computeResult,
  estimationsAreZeroOrSame,
  getArticleTitleFromPzPluginData,
  getArticleUrlFromPzPluginData,
  getPriceFromPzPluginData,
  parseLocaleNumber,
  PzPluginData,
  replaceDoubleCommas,
  Result,
  SoundFile,
  Winner,
} from '../../utils/utils';
import { NotificationService } from '../../service/notification.service';
import { GENERAL_ERROR_SUMMARY } from '../../utils/error-handler';
import { Howl } from 'howler';
import { interval, lastValueFrom, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { PzService } from '../../service/pz.service';
import { StatsService } from '../../service/stats.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
})
export class HomeComponent implements OnInit, OnDestroy {
  private assetsDir = 'assets/mp3/';
  public soundWin = new Howl({
    src: [`${this.assetsDir}/${SoundFile.WIN}`],
    html5: true,
  });
  public soundExtraWin = new Howl({
    src: [`${this.assetsDir}/${SoundFile.EXTRA_WIN}`],
    html5: true,
  });
  public soundSenfkarteWin = new Howl({
    src: [`${this.assetsDir}/${SoundFile.SENFKARTE_WIN}`],
    html5: true,
  });
  public soundGameWin = new Howl({
    src: [`${this.assetsDir}/${SoundFile.APPLAUSE}`],
    html5: true,
  });
  public soundError = new Howl({
    src: [`${this.assetsDir}/${SoundFile.ERROR}`],
    html5: true,
  });

  public pollAmazonPrice$ = undefined;
  public error$ = new Subject();

  public animatePlayer1 = false;
  public animatePlayer2 = false;
  public animatePointsPlayer1 = false;
  public animatePointsPlayer2 = false;
  public animateWinnerPlayer1 = false;
  public animateWinnerPlayer2 = false;
  public animateCurrentPlayer1 = false;
  public animateCurrentPlayer2 = false;
  public maskPriceShowed = false;

  private animationPlayerTimeout = 1500;
  private animationPointsTimeout = 400;

  constructor(
    private notificationService: NotificationService,
    public pzService: PzService,
    public statsService: StatsService
  ) {
    this.pzService.initModel();
  }

  public async ngOnInit(): Promise<void> {
    this.initStartPlayerAnimation();

    this.pollAmazonPrice$ = interval(250)
      .pipe(
        takeUntil(this.error$),
        tap(async () => {
          if (!estimationsAreZeroOrSame(this.pzService.model)) {
            const data = await lastValueFrom(this.statsService.getPrice());
            if (Object.keys(data).length !== 0) {
              await this.calculateDifference(data);
            }
          }
        })
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.pollAmazonPrice$.unsubscribe();
  }

  // Don't allow negative values in decimal input
  @HostListener('document:keydown', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): boolean {
    return event.key !== '-';
  }

  async calculateDifference(pzPluginData: PzPluginData): Promise<void> {
    const price = getPriceFromPzPluginData(pzPluginData);
    // Value is initialized after calculated difference; do not calculate difference if value is initialized
    if (price === null || estimationsAreZeroOrSame(this.pzService.model)) {
      return;
    }

    const parsedPrice: number = parseLocaleNumber(price);
    if (!isNaN(parsedPrice)) {
      this.pzService.updateAmazonPrice(parsedPrice);
      // console.log("Submitted form: %s: %f, %s: %f, %s: %f", 'Spieler 1', this.model.estimatedPricePlayer1, 'Spieler 2', this.model.estimatedPricePlayer2, 'Amazon-Preis', this.model.priceAmazon);
      const result = computeResult(this.pzService.model);
      const articleUrl = getArticleUrlFromPzPluginData(pzPluginData);
      const articleTitle = getArticleTitleFromPzPluginData(pzPluginData);
      this.pzService.updateStatistics(result, articleUrl, articleTitle);
      this.playSound(result);
      console.log('Result:', result);
      this.showResult(result);
      this.animateImage(result);
      await this.clearForm();
      this.pzService.updateCurrentPlayer();
      this.initStartPlayerAnimation();
      this.resetPriceMask();
      if (this.pzService.isGameFinished()) {
        this.showWinnerAnimation();
      }
    } else {
      console.error('Could not parse value:', parsedPrice);
      this.notificationService.showError(
        GENERAL_ERROR_SUMMARY,
        'Differenz konnte nicht berechnet werden. Kein gültiger Amazon-Preis.'
      );
      this.playSound();
    }
  }

  public async clearForm(): Promise<void> {
    this.pzService.resetModel();
    try {
      await lastValueFrom(this.statsService.resetPrice());
    } catch (err: any) {
      console.error('Could not reset price', err);
    }
  }

  public showResult(result: Result): void {
    if (result.winner === Winner.BOTH) {
      this.pzService.updatePointsPlayer1(result);
      this.pzService.updatePointsPlayer2(result);
    } else if (result.winner === Winner.PLAYER_1) {
      this.pzService.updatePointsPlayer1(result);
    } else {
      this.pzService.updatePointsPlayer2(result);
    }
  }

  public initStartPlayerAnimation(): void {
    if (this.pzService.startPlayer === 1) {
      this.animateCurrentPlayer1 = true;
      this.animateCurrentPlayer2 = false;
    } else {
      this.animateCurrentPlayer1 = false;
      this.animateCurrentPlayer2 = true;
    }
  }

  public animateImage(result: Result): void {
    if (result.winner === Winner.BOTH) {
      this.animatePlayer1 = true;
      this.animatePlayer2 = true;
      this.animatePointsPlayer1 = true;
      this.animatePointsPlayer2 = true;
    } else if (result.winner === Winner.PLAYER_1) {
      this.animatePlayer1 = true;
      this.animatePointsPlayer1 = true;
    } else {
      this.animatePlayer2 = true;
      this.animatePointsPlayer2 = true;
    }

    setTimeout(() => {
      this.animatePointsPlayer1 = false;
      this.animatePointsPlayer2 = false;
    }, this.animationPointsTimeout);

    setTimeout(() => {
      this.animatePlayer1 = false;
      this.animatePlayer2 = false;
    }, this.animationPlayerTimeout);
  }

  public showWinnerAnimation(): void {
    setTimeout(() => {
      this.animateCurrentPlayer1 = false;
      this.animateCurrentPlayer2 = false;
      const gameWinner: Winner = this.pzService.getGameWinner();
      if (gameWinner === Winner.BOTH) {
        this.animateWinnerPlayer1 = true;
        this.animateWinnerPlayer2 = true;
      } else if (gameWinner === Winner.PLAYER_1) {
        this.animateWinnerPlayer1 = true;
      } else {
        this.animateWinnerPlayer2 = true;
      }

      if (this.pzService.soundOn) {
        this.soundGameWin.play();
      }
    }, this.animationPlayerTimeout);
  }

  private playSound(result?: Result): void {
    if (this.pzService.soundOn) {
      if (!result) {
        this.soundError.play();
      } else {
        if (result.sound === SoundFile.WIN) {
          this.soundWin.play();
        } else if (result.sound === SoundFile.EXTRA_WIN) {
          this.soundExtraWin.play();
        } else if (result.sound === SoundFile.SENFKARTE_WIN) {
          this.soundSenfkarteWin.play();
        }
      }
    }
  }

  public setSenfkartePlayer1(): void {
    this.pzService.setSenfkartePlayer1();
  }

  public setSenfkartePlayer2(): void {
    this.pzService.setSenfkartePlayer2();
  }

  public isSenfkartePlayer1(): boolean {
    return this.pzService.isSenfkartePlayer1();
  }

  public isSenfkartePlayer2(): boolean {
    return this.pzService.isSenfkartePlayer2();
  }

  public isSenfkartePlayer1Possible(): boolean {
    return this.pzService.isSenfkartePlayer1Possible();
  }

  public isSenfkartePlayer2Possible(): boolean {
    return this.pzService.isSenfkartePlayer2Possible();
  }

  public isWinnerAnimationStarted(): boolean {
    return this.animateWinnerPlayer1 || this.animateWinnerPlayer2;
  }

  /*
   * See custom directive: [pzPasswordEye]
   * If the primeng password input eye is clicked, we switch to "normal input behaviour".
   * Thus, for both inputs the unmasked price is shown.
   */
  public togglePriceMask(): void {
    this.maskPriceShowed = true;
    this.pzService.passwordPriceInputMode = false;
  }

  /*
   * See custom directive: [pzPasswordEye]
   * Reset to password input mode after computing the match results
   * (only if we were in password mode by default before unmasking the prices)
   */
  public resetPriceMask(): void {
    if (this.maskPriceShowed) {
      this.pzService.passwordPriceInputMode = true;
    }
  }

  public onChangePasswordInput(): void {
    if (this.pzService.model.estimatedPricePlayer1_password !== undefined) {
      const parsedPricePlayer1 = parseLocaleNumber(
        replaceDoubleCommas(this.pzService.model.estimatedPricePlayer1_password)
      );

      if (!isNaN(parsedPricePlayer1)) {
        this.pzService.model.estimatedPricePlayer1 = parsedPricePlayer1;
      }
    }

    if (this.pzService.model.estimatedPricePlayer2_password !== undefined) {
      const parsedPricePlayer2 = parseLocaleNumber(
        replaceDoubleCommas(this.pzService.model.estimatedPricePlayer2_password)
      );

      if (!isNaN(parsedPricePlayer2)) {
        this.pzService.model.estimatedPricePlayer2 = parsedPricePlayer2;
      }
    }
  }

  public onKeyDownPasswordInput(event: KeyboardEvent): boolean {
    /*
     * TODO: password input regex
     * Use regex to detect repeated or multiple commas and single comma at first or last position
     * Maybe this, but "1," is not allowed in input, sadly. /(?<!,)\b[0-9]+(?:,[0-9]+)?\b(?!,)$/
     */
    const regex = new RegExp(/^[0-9,]*$/); // Allow all numbers and comma
    const allowedKeys = [
      'Delete',
      'Home',
      'End',
      'ArrowLeft',
      'ArrowRight',
      'Backspace',
      'Enter',
      'Tab',
    ];
    if (allowedKeys.includes(event.key)) {
      return true;
    }

    // TODO: ^ key recognization does not work
    const notAllowedKeys = ['Dead'];
    if (notAllowedKeys.includes(event.key)) {
      event.preventDefault();
      return false;
    }

    if (regex.test(event.key)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }
}
